import { DNode } from '@dojo/framework/core/interfaces';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { create, diffProperty, invalidator, v, w } from '@dojo/framework/core/vdom';
import { formatAriaProperties } from '@dojo/widgets/common/util';
import { publish } from '@holmescorp/pub-sub';

import css from '../css/editor.m.css';
import { EditorToolbar } from './toolbar';

export interface EditorProperties<T extends { content: any } = { content: string }> {
	/** Id of the contenteditable element */
	editorId?: string;

	/** Custom aria attributes */
	aria?: { [key: string]: string | null };

	/** The disabled property of the container */
	disabled?: boolean;

	/** The initial value */
	initialContent?: T['content'];

	/** The controlled value */
	content?: T['content'];

	/** Callback fired when the value changes */
	onContent?: (value: T['content']) => void;

	/** Placeholder text */
	placeholder?: string;

	/** Tab order the widget should follow  */
	tabIndex?: number;

	/** CSS classes to be applied */
	classes?: string[];

	/** The id to be applied to the input */
	widgetId?: string;
}

interface EditorICache {
	content: string;
	editable: DNode;
	contentInEditor: boolean;
	initialContent: string;
}

const factory = create({
	theme,
	icache: createICacheMiddleware<EditorICache>(),
	diffProperty,
	invalidator
}).properties<EditorProperties>();

export const Editor = factory(({ middleware: { icache, theme, diffProperty, invalidator }, properties, children }) => {
	const {
		editorId = 'editor',
		aria = {},
		disabled = false,
		initialContent,
		placeholder,
		tabIndex,
		classes = [],
		widgetId
	} = properties();

	let { content } = properties();
	if (content === undefined) {
		content = icache.get('content');
		const existingInitialContent = icache.get('initialContent');

		if (initialContent !== existingInitialContent) {
			icache.set('content', initialContent as string);
			icache.set('initialContent', initialContent as string);
			content = initialContent;
		}
	} else {
		// If content prop is controlled, detect changes and invalidate cache
		diffProperty('content', properties, (current, next) => {
			if (current.content !== next.content) {
				icache.delete('editable');
				invalidator();
			}
		});
	}

	const { root, editorRoot } = theme.classes(css);

	return v(
		'div',
		{
			xqa: 'editor',
			widgetId,
			disabled,
			tabIndex,
			classes: [...classes, root, theme.variant()],
			...formatAriaProperties(aria)
		},
		[
			w(EditorToolbar, {}),
			icache.getOrSet(
				'editable',
				v('div', {
					id: editorId,
					placeholder,
					classes: [editorRoot],
					contentEditable: true,
					role: 'textbox',
					onselectstart: () => {
						// Watch for selections and store it off.

						const getSelection = () => {
							const selection = document.getSelection();

							// Pub it so the toolbar can work on it if needed.
							publish('current:selection', selection && selection.getRangeAt(0));

							// Cleanup the listeners.
							document.removeEventListener('keyup', getSelection);
							document.removeEventListener('mouseup', getSelection);
						};

						document.addEventListener('keyup', getSelection);
						document.addEventListener('mouseup', getSelection);
					},
					oninput: (event: Event) => {
						const { onContent } = properties();
						event.stopPropagation();
						const content = (event.target as HTMLInputElement).innerHTML;
						icache.set('content', content);
						onContent && onContent(content);
					},
					onblur: (event: Event) => {
						const { onContent } = properties();
						event.stopPropagation();
						const content = (event.target as HTMLInputElement).innerHTML;
						icache.set('content', content);
						onContent && onContent(content);
					},
					innerHTML: content
				})
			),
			v('div', {}, children())
		]
	);
});

export default Editor;
